/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Inventor Automation team
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import * as signalR from '@aspnet/signalr';
import repo from './Repository';

class JobManager {

    async startConnection() {
        const token = localStorage.getItem('token');
        const connection = new signalR.HubConnectionBuilder()
                            .withUrl('/signalr/connection', {
                                accessTokenFactory: () => token
                            })
                            .configureLogging(signalR.LogLevel.Warning)
                            .build();

        await connection.start();
        return connection;
    }

    async doUpdateJob(projectId, parameters, onStart, onComplete, onError) {
        var completed = false;

        try {
            const connection = await this.startConnection();

            if (onStart)
                onStart();

            connection.onclose(function() {
                // Don't treat connection close as error if job already completed
            });

            connection.on("onComplete", (updatedState, stats, reportUrl) => {
                completed = true;
                try { connection.stop(); } catch (e) { /* ignore */ }

                if (onComplete)
                    onComplete(updatedState, stats, reportUrl);
            });

            connection.on("onError", (errorData) => {
                completed = true;
                try { connection.stop(); } catch (e) { /* ignore */ }

                if (onError)
                    onError(errorData);
            });

            await connection.invoke('CreateUpdateJob', projectId, parameters, repo.getAccessCode());
        } catch (err) {
            if (!completed) {
                console.log('doUpdateJob connection error:', err.message);
                if (onError)
                    onError({ title: 'Connection error', messages: [err.message] });
            }
        }
    }


    async doAdoptJob(packageId, onStart, onComplete, onError) {
        var completed = false;
        var connectionClosed = false;

        try {
            const connection = await this.startConnection();

            if (onStart)
                onStart();

            // Handle connection close - don't treat as error if job completed
            connection.onclose(function(error) {
                connectionClosed = true;
                if (!completed && error) {
                    console.log('SignalR connection closed:', error);
                    // Don't call onError here - the job may have completed successfully
                    // The auto-import will still work because the server processed the job
                }
            });

            connection.on("onComplete", (newProject, stats, reportUrl) => {
                completed = true;
                // stop connection
                try {
                    connection.stop();
                } catch (e) {
                    // Ignore stop errors
                }

                if (onComplete)
                    onComplete(newProject, stats, reportUrl);
            });

            connection.on("onError", (errorData) => {
                completed = true;
                try {
                    connection.stop();
                } catch (e) {
                    // Ignore stop errors
                }

                if (onError)
                    onError(errorData);
            });

            await connection.invoke('CreateAdoptJob', packageId, repo.getAccessCode());
        } catch (err) {
            // If invoke fails but connection closed, the job may still be processing
            // Don't immediately error - log and let the process continue
            console.log('SignalR invoke error (job may still be processing):', err.message);
            
            // Only call onError if we haven't completed and this is a real connection failure
            if (!completed && !connectionClosed) {
                if (onError)
                    onError({ title: 'Connection error', messages: [err.message] });
            }
        }
    }

    async doAdoptWithParameters(parameters, onStart, onComplete, onError) {
        var completed = false;

        try {
            const connection = await this.startConnection();

            if (onStart)
                onStart();

            connection.onclose(function() {
                // Don't treat connection close as error if job already completed
            });

            connection.on("onComplete", (newProject) => {
                completed = true;
                try { connection.stop(); } catch (e) { /* ignore */ }

                if (onComplete)
                    onComplete(newProject);
            });

            connection.on("onError", (jobId, reportUrl) => {
                completed = true;
                try { connection.stop(); } catch (e) { /* ignore */ }

                if (onError)
                    onError(jobId, reportUrl);
            });

            await connection.invoke("CreateAdoptProjectWithParametersJob", parameters, repo.getAccessCode());
        } catch (err) {
            if (!completed) {
                console.log('doAdoptWithParameters connection error:', err.message);
                if (onError)
                    onError({ title: 'Connection error', messages: [err.message] });
            }
        }
    }

    /**
     * Generic way to generate a download and get URL to it.
     *
     * @param methodName SignalR method to call.
     * @param projectId  Project ID.
     * @param hash       Parameters hash.
     * @param onStart    Callback to be called on start. No arguments.
     * @param onSuccess  Callback to be called on success. Argument: url to the generated download.
     * @param onError    Callback to be called on error. Arguments: job ID, report url.
     * */
    async doDownloadJob(methodName, projectId, hash, key, onStart, onSuccess, onError) {
        var completed = false;

        try {
            const connection = await this.startConnection();

            if (onStart) onStart();

            connection.onclose(function() {
                // Don't treat connection close as error if job already completed
            });

            connection.on("onComplete", (downloadUrl, stats, reportUrl) => {
                completed = true;
                try { connection.stop(); } catch (e) { /* ignore */ }

                if (! downloadUrl && ! stats) {
                    if (onError) onError("no outputs", "Downloads are not found");
                    return;
                }

                if (onSuccess) {
                    const code = repo.getAccessCode();
                    if (code) {
                        downloadUrl += "/" + code;
                    }
                    onSuccess(downloadUrl, stats, reportUrl);
                }
            });

            connection.on("onError", (errorData) => {
                completed = true;
                try { connection.stop(); } catch (e) { /* ignore */ }
                if (onError) onError(errorData);
            });

            if (key != null)
                await connection.invoke(methodName, projectId, hash, key, repo.getAccessCode());
            else
                await connection.invoke(methodName, projectId, hash, repo.getAccessCode());
        } catch (err) {
            // If the connection was stopped after onComplete/onError, the invoke
            // rejects with "Invocation canceled". This is expected — not a real error.
            if (!completed) {
                console.log('doDownloadJob connection error:', err.message);
                if (onError)
                    onError({ title: 'Connection error', messages: [err.message] });
            }
        }
    }

    async doDrawingExportJob(projectId, hash, drawingKey, onStart, onComplete, onError) {
        var completed = false;

        try {
            const connection = await this.startConnection();

            if (onStart)
                onStart();

            connection.onclose(function() {
                // Don't treat connection close as error if job already completed
            });

            connection.on("onComplete", (drawingUrl, stats, reportUrl) => {
                completed = true;
                try { connection.stop(); } catch (e) { /* ignore */ }

                if (onComplete) {
                    onComplete(drawingUrl, stats, reportUrl);
                }
            });

            connection.on("onError", (errorData) => {
                completed = true;
                try { connection.stop(); } catch (e) { /* ignore */ }

                if (onError)
                    onError(errorData);
            });

            await connection.invoke('CreateDrawingPdfJob', projectId, hash, drawingKey, repo.getAccessCode());
        } catch (err) {
            if (!completed) {
                console.log('doDrawingExportJob connection error:', err.message);
                if (onError)
                    onError({ title: 'Connection error', messages: [err.message] });
            }
        }
    }
}

const jobManager = new JobManager();

export function Jobs() {
  return jobManager;
}